<template>
  <v-container class="guest-room">
    <v-card class="pa-4 pa-md-6">
      <div class="d-flex align-center guest-room__head">
        <v-avatar v-if="psychologist.photo" size="56" class="mr-3">
          <img :src="psychologist.photo" :alt="psychologist.full_name" />
        </v-avatar>
        <div>
          <p class="title mb-0">{{ t("title") }}</p>
          <p v-if="psychologist.full_name" class="mb-0 text-body-2">
            {{ t("with") }}: {{ psychologist.full_name }}
          </p>
        </div>
      </div>

      <p v-if="notFound" class="mt-6 text-center">{{ t("notFound") }}</p>

      <template v-else>
        <div v-if="stage === 'lobby'" class="guest-room__lobby mt-6">
          <v-text-field
            v-model="guestName"
            :label="t('yourName')"
            outlined
            dense
            maxlength="100"
          ></v-text-field>
          <p v-if="!room.is_open" class="text-body-2">{{ t("waitingHost") }}</p>
          <p v-else-if="room.busy" class="text-body-2">{{ t("busy") }}</p>
          <p v-if="mediaError" class="error--text">{{ t("cameraError") }}</p>
          <v-btn
            color="primary"
            depressed
            block
            :loading="joining"
            :disabled="!room.is_open || room.busy"
            @click="join"
          >
            {{ t("join") }}
          </v-btn>
        </div>

        <div v-else-if="stage === 'call'" class="mt-4">
          <VideoCall
            :local-stream="localStream"
            :remote-stream="remoteStream"
            :status="statusText"
            :waiting="!remoteStream"
            :mic-title="t('mic')"
            :cam-title="t('camera')"
          >
            <template #actions>
              <v-btn small rounded color="error" @click="leave">
                <v-icon left small>mdi-phone-off</v-icon>{{ t("leave") }}
              </v-btn>
            </template>
          </VideoCall>
        </div>

        <div v-else class="mt-6 text-center">
          <p>{{ endText }}</p>
          <v-btn color="primary" depressed @click="stage = 'lobby'">{{ t("rejoin") }}</v-btn>
        </div>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import RoomServices from "@/services/api/room.services";
import VideoCall from "@/components/VideoRoom/VideoCall";
import { RoomPeer, getLocalStream, randomId } from "@/components/VideoRoom/roomPeer";

const POLL_FAST = 1000;
const POLL_SLOW = 2500;
const LOBBY_POLL = 4000;

function storage(key, value) {
  try {
    if (value === undefined) return window.sessionStorage.getItem(key);
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    return null;
  }
  return null;
}

export default {
  name: "GuestRoom",
  components: { VideoCall },
  metaInfo() {
    return { title: "Платформа / " + this.t("title") };
  },
  data() {
    return {
      token: this.$route.params.token,
      room: {},
      notFound: false,
      stage: "lobby",
      endText: "",
      guestId: storage("room-guest-id") || randomId(),
      guestName: storage("room-guest-name") || "",
      joining: false,
      mediaError: false,
      localStream: null,
      remoteStream: null,
      peer: null,
      connected: false,
      lastSignalId: 0,
      pollTimer: null,
      lobbyTimer: null,
      destroyed: false,
    };
  },
  computed: {
    psychologist() {
      return this.room.psychologist || {};
    },
    statusText() {
      return this.connected ? this.t("connected") : this.t("connecting");
    },
  },
  mounted() {
    storage("room-guest-id", this.guestId);
    window.addEventListener("pagehide", this.onPageHide);
    this.refreshLobby();
  },
  beforeDestroy() {
    this.destroyed = true;
    window.removeEventListener("pagehide", this.onPageHide);
    clearTimeout(this.lobbyTimer);
    if (this.stage === "call") {
      RoomServices.guestLeave(this.token, this.guestId).catch(() => {});
    }
    this.stopCall();
  },
  methods: {
    t(key) {
      return this.$vuetify.lang.t(`$vuetify.view.room.${key}`);
    },
    refreshLobby() {
      clearTimeout(this.lobbyTimer);
      RoomServices.guestShow(this.token)
        .then((response) => {
          this.room = response.data.room;
        })
        .catch(() => {
          this.notFound = true;
        })
        .finally(() => {
          if (!this.destroyed && !this.notFound && this.stage !== "call") {
            this.lobbyTimer = setTimeout(this.refreshLobby, LOBBY_POLL);
          }
        });
    },
    async join() {
      this.joining = true;
      this.mediaError = false;
      storage("room-guest-name", this.guestName);

      try {
        if (!this.localStream) {
          this.localStream = await getLocalStream();
        }
      } catch (e) {
        this.mediaError = true;
        this.joining = false;
        return;
      }

      this.enter(true)
        .then(() => {
          clearTimeout(this.lobbyTimer);
          this.stage = "call";
          this.schedulePoll(0);
        })
        .catch(() => {
          this.refreshLobby();
        })
        .finally(() => {
          this.joining = false;
        });
    },
    enter(fresh) {
      return RoomServices.guestJoin(this.token, this.guestId, this.guestName, fresh).then(
        (response) => {
          this.room = response.data.room;
          this.lastSignalId = response.data.last_signal_id || 0;
        }
      );
    },
    schedulePoll(delay) {
      clearTimeout(this.pollTimer);
      if (this.destroyed || this.stage !== "call") return;
      this.pollTimer = setTimeout(this.poll, delay);
    },
    poll() {
      RoomServices.guestPoll(this.token, this.guestId, this.lastSignalId)
        .then((response) => {
          const { room, signals, in_room: inRoom } = response.data;
          this.room = room;

          signals.forEach((signal) => {
            if (this.stage !== "call") return;
            this.lastSignalId = Math.max(this.lastSignalId, signal.id);
            this.handleSignal(signal.payload);
          });
          if (this.stage !== "call") return null;

          if (!inRoom) {
            // psychologist reopened the room (new session): enter again
            if (room.is_open) {
              this.dropPeer();
              return this.enter(true).catch(() => this.end(this.t("busy")));
            }
            this.end(this.t("waitingHost"));
          }
          return null;
        })
        .catch(() => {})
        .finally(() => {
          this.schedulePoll(this.connected ? POLL_SLOW : POLL_FAST);
        });
    },
    handleSignal(payload) {
      if (payload.type === "kicked") {
        this.end(this.t("kicked"));
        return;
      }
      if (payload.type === "offer") {
        this.startPeer();
      }
      if (this.peer) {
        this.peer.handle(payload);
      }
    },
    startPeer() {
      this.dropPeer();
      this.peer = new RoomPeer({
        initiator: false,
        stream: this.localStream,
        send: (payload) =>
          RoomServices.guestSignal(this.token, this.guestId, payload).catch(() => {}),
        onStream: (stream) => {
          this.remoteStream = stream;
        },
        onConnect: () => {
          this.connected = true;
        },
        onClose: () => {
          this.remoteStream = null;
          this.connected = false;
        },
      });
    },
    dropPeer() {
      if (this.peer) this.peer.destroy();
      this.peer = null;
      this.remoteStream = null;
      this.connected = false;
    },
    leave() {
      RoomServices.guestLeave(this.token, this.guestId).catch(() => {});
      this.end(this.t("left"));
    },
    end(text) {
      this.endText = text;
      this.stage = "ended";
      this.stopCall();
      this.refreshLobby();
    },
    stopCall() {
      clearTimeout(this.pollTimer);
      this.dropPeer();
      if (this.localStream) {
        this.localStream.getTracks().forEach((track) => track.stop());
        this.localStream = null;
      }
    },
    onPageHide() {
      if (this.stage === "call" && navigator.sendBeacon) {
        const base = process.env.VUE_APP_API.replace(/\/+$/, "");
        const body = new FormData();
        body.append("guest_id", this.guestId);
        navigator.sendBeacon(`${base}/api/rooms/${this.token}/leave`, body);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.guest-room {
  max-width: 960px;
}
.guest-room__lobby {
  max-width: 420px;
  margin: 0 auto;
}
</style>
